// FwTable — the Session B dense table pattern.
//
// Replaces AgTable page-by-page (Session C migrates the rest). Data access
// is injected via `fetcher`, so the REAL pages pass an api.call wrapper with
// the exact request shape AgTable already used, and the design preview
// passes a stub — the component itself never talks to the network.
//
// Pattern features:
//  - foundation density (37px pointer rows, 44px touch targets via CSS)
//  - sortable headers, hover row actions, whole-row open (side-panel hook)
//  - saved views: named sort+filter sets persisted per module (localStorage)
//  - quick text filter, debounced; extra filters via render prop
//
// Motion: rows/hover/state only — tables render dozens of times an hour and
// never animate on entrance.
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";

export type FwColumn = {
  label: string;
  key: string;
  sort?: boolean;
  type?: "text" | "link" | "date" | "date_time" | "locality" | "obj" | "bool" | "num";
  /** Hide below this breakpoint to keep phone tables scannable. */
  minWidth?: number;
  /** Custom cell content. Takes precedence over `type` — for state a reader
   *  should catch by glancing (a badge, an icon) rather than by reading. */
  render?: (row: any) => ReactNode;
};

export type FwQuery = {
  page: number; // 0-based
  limit: number;
  sortBy: string;
  sortDir: "asc" | "desc";
  filter: Record<string, any>;
};

export type FwFetchResult = { rows: any[]; total: number };

export type FwView = {
  name: string;
  sortBy: string;
  sortDir: "asc" | "desc";
  filter: Record<string, any>;
};

type Props = {
  module: string; // storage key namespace
  columns: FwColumn[];
  fetcher: (q: FwQuery, cb: (r: FwFetchResult) => void) => void;
  defaultSort?: { sortBy: string; sortDir: "asc" | "desc" };
  /** Quick text filter wiring: which filter key the search box writes. */
  quickFilterKey?: string;
  quickFilterPlaceholder?: string;
  /** Extra filter controls (locality pickers etc.) */
  extraFilters?: (filter: Record<string, any>, set: (patch: Record<string, any>) => void) => ReactNode;
  /** Row actions (hover on desktop, always visible on touch). */
  actions?: (row: any) => ReactNode;
  /** Whole-row open — the side-panel hook. */
  onRowOpen?: (row: any) => void;
  toolbar?: ReactNode;
  emptyText?: string;
  /** Row selection + bulk delete. Called with selected ids; call done() to
   *  clear selection and reload. */
  onDeleteSelected?: (ids: string[], done: () => void) => void;
  /** Extra controls in the toolbar, given the current selection and the live
   *  filter. `done()` clears the selection and reloads, same as delete. The
   *  filter is passed so an export can cover exactly what is on screen. */
  bulkActions?: (
    ids: string[],
    done: () => void,
    filter: Record<string, any>
  ) => ReactNode;
  /** Bump to force a reload (e.g. after an external mutation). */
  reloadKey?: any;
};

const viewsKey = (m: string) => `fw:views:${m}`;

function loadViews(module: string): FwView[] {
  try {
    return JSON.parse(localStorage.getItem(viewsKey(module)) || "[]");
  } catch {
    return [];
  }
}

function cellValue(row: any, col: FwColumn) {
  const v = row[col.key];
  switch (col.type) {
    case "date":
      return v ? moment(v).format("DD MMM YYYY") : "—";
    case "date_time":
      return v ? moment(v).format("DD MMM, h:mm A") : "—";
    case "locality":
      return v?.name || "—";
    case "obj":
      return v ? [v.firstName, v.lastName].filter(Boolean).join(" ") || "—" : "—";
    case "bool":
      return v ? "Yes" : "No";
    default:
      return v ?? "—";
  }
}

export default function FwTable(props: Props) {
  const {
    module,
    columns,
    fetcher,
    defaultSort = { sortBy: "createdAt", sortDir: "desc" as const },
    quickFilterKey,
    quickFilterPlaceholder,
    extraFilters,
    actions,
    onRowOpen,
    toolbar,
    emptyText = "Nothing here yet.",
    onDeleteSelected,
    bulkActions,
    reloadKey,
  } = props;

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState(defaultSort.sortBy);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(defaultSort.sortDir);
  const [filter, setFilter] = useState<Record<string, any>>({});
  const [quick, setQuick] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // saved views
  const [views, setViews] = useState<FwView[]>(() => loadViews(module));
  const [activeView, setActiveView] = useState<string | null>(null);
  const [savingView, setSavingView] = useState(false);
  const [viewName, setViewName] = useState("");

  const debounce = useRef<any>(null);

  // FwColumn has carried a `minWidth` ("hide below this breakpoint") since the
  // Session B rebuild, but nothing ever read it, so every column rendered at
  // every size. Honouring it is what lets a page add a secondary column — an
  // audit timestamp, say — without shoving the columns that matter off a phone.
  const [viewport, setViewport] = useState(() =>
    typeof window === "undefined" ? 1440 : window.innerWidth
  );
  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const shownColumns = useMemo(
    () => columns.filter((c) => !c.minWidth || viewport >= c.minWidth),
    [columns, viewport]
  );
  const spanAll =
    shownColumns.length + (actions ? 1 : 0) + (onDeleteSelected ? 1 : 0);

  const load = useCallback(
    (q: FwQuery) => {
      setLoading(true);
      fetcher(q, (r) => {
        setRows(r.rows);
        setTotal(r.total);
        setLoading(false);
      });
    },
    [fetcher]
  );

  useEffect(() => {
    setSelected(new Set());
    load({ page, limit, sortBy, sortDir, filter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sortBy, sortDir, filter, load, reloadKey]);

  // debounced quick filter
  const onQuick = (value: string) => {
    setQuick(value);
    if (!quickFilterKey) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setPage(0);
      setActiveView(null);
      setFilter((f) => ({ ...f, [quickFilterKey]: value }));
    }, 250);
  };

  const patchFilter = (patch: Record<string, any>) => {
    setPage(0);
    setActiveView(null);
    setFilter((f) => ({ ...f, ...patch }));
  };

  const onSort = (col: FwColumn) => {
    if (!col.sort) return;
    if (sortBy === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col.key);
      setSortDir("asc");
    }
    setPage(0);
  };

  // ── saved views ────────────────────────────────────────────────────────
  const applyView = (v: FwView | null) => {
    setPage(0);
    if (!v) {
      setActiveView(null);
      setQuick("");
      setSortBy(defaultSort.sortBy);
      setSortDir(defaultSort.sortDir);
      setFilter({});
      return;
    }
    setActiveView(v.name);
    setQuick(quickFilterKey ? v.filter[quickFilterKey] || "" : "");
    setSortBy(v.sortBy);
    setSortDir(v.sortDir);
    setFilter(v.filter);
  };

  const saveView = () => {
    const name = viewName.trim();
    if (!name) return;
    const v: FwView = { name, sortBy, sortDir, filter };
    const next = [...views.filter((x) => x.name !== name), v];
    setViews(next);
    localStorage.setItem(viewsKey(module), JSON.stringify(next));
    setActiveView(name);
    setSavingView(false);
    setViewName("");
  };

  const deleteView = (name: string) => {
    const next = views.filter((x) => x.name !== name);
    setViews(next);
    localStorage.setItem(viewsKey(module), JSON.stringify(next));
    if (activeView === name) applyView(null);
  };

  const hasCustomState =
    Object.keys(filter).some((k) => {
      const v = filter[k];
      return Array.isArray(v) ? v.length > 0 : Boolean(v);
    }) ||
    sortBy !== defaultSort.sortBy ||
    sortDir !== defaultSort.sortDir;

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r._id));
  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPage) rows.forEach((r) => next.delete(r._id));
      else rows.forEach((r) => next.add(r._id));
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const pageNumbers = useMemo(() => {
    const nums: (number | "…")[] = [];
    const push = (n: number | "…") => nums[nums.length - 1] !== n && nums.push(n);
    for (let i = 0; i < totalPages; i++) {
      if (i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1) push(i);
      else push("…");
    }
    return nums;
  }, [page, totalPages]);

  return (
    <div className="fw-table">
      {/* toolbar: quick filter + extra filters + page actions */}
      <div className="fw-table__bar">
        {quickFilterKey && (
          <div className="fw-table__search">
            <i className="ri-search-line" aria-hidden />
            <input
              value={quick}
              placeholder={quickFilterPlaceholder || "Filter…"}
              onChange={(e) => onQuick(e.target.value)}
              aria-label="Quick filter"
            />
          </div>
        )}
        {extraFilters?.(filter, patchFilter)}
        <div className="fw-table__bar-spacer" />
        {/* Bulk actions sit beside Delete and share its selection, so a page can
            add non-destructive operations without owning the whole table. */}
        {bulkActions?.([...selected], () => setSelected(new Set()), filter)}
        {onDeleteSelected && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            disabled={selected.size === 0}
            onClick={() =>
              onDeleteSelected([...selected], () => setSelected(new Set()))
            }
          >
            <i className="ri-delete-bin-6-line" /> Delete
            {selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
        )}
        {toolbar}
      </div>

      {/* saved views */}
      <div className="fw-table__views" role="tablist" aria-label="Saved views">
        <button
          type="button"
          role="tab"
          aria-selected={!activeView}
          className={`fw-viewchip${!activeView ? " fw-viewchip--active" : ""}`}
          onClick={() => applyView(null)}
        >
          All
        </button>
        {views.map((v) => (
          <span
            key={v.name}
            className={`fw-viewchip${activeView === v.name ? " fw-viewchip--active" : ""}`}
          >
            <button type="button" role="tab" aria-selected={activeView === v.name} onClick={() => applyView(v)}>
              {v.name}
            </button>
            <button
              type="button"
              className="fw-viewchip__x"
              aria-label={`Delete view ${v.name}`}
              onClick={() => deleteView(v.name)}
            >
              ×
            </button>
          </span>
        ))}
        {savingView ? (
          <span className="fw-viewchip fw-viewchip--input">
            <input
              autoFocus
              value={viewName}
              placeholder="View name…"
              onChange={(e) => setViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveView();
                if (e.key === "Escape") setSavingView(false);
              }}
              aria-label="New view name"
            />
            <button type="button" onClick={saveView}>Save</button>
          </span>
        ) : (
          hasCustomState && (
            <button
              type="button"
              className="fw-viewchip fw-viewchip--save"
              onClick={() => setSavingView(true)}
            >
              + Save view
            </button>
          )
        )}
      </div>

      {/* table */}
      <div className="fw-table__scroll">
        <table>
          <thead>
            <tr>
              {onDeleteSelected && (
                <th className="fw-th--check">
                  <input
                    type="checkbox"
                    checked={allOnPage}
                    onChange={togglePage}
                    aria-label="Select all on page"
                  />
                </th>
              )}
              {shownColumns.map((c) => (
                <th
                  key={c.key}
                  className={c.sort ? "fw-th--sortable" : undefined}
                  aria-sort={
                    sortBy === c.key
                      ? sortDir === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                  onClick={() => onSort(c)}
                >
                  {c.label}
                  {sortBy === c.key && (
                    <i
                      className={
                        sortDir === "asc" ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                      }
                    />
                  )}
                </th>
              ))}
              {actions && <th className="fw-th--actions" aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={spanAll} className="fw-table__state">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={spanAll} className="fw-table__state">
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={row._id || i}
                  className={onRowOpen ? "fw-tr--openable" : undefined}
                  onClick={() => onRowOpen?.(row)}
                >
                  {onDeleteSelected && (
                    <td
                      className="fw-td--check"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(row._id)}
                        onChange={() => toggleRow(row._id)}
                        aria-label="Select row"
                      />
                    </td>
                  )}
                  {shownColumns.map((c) => (
                    <td key={c.key} className={c.type === "num" ? "fw-num" : undefined}>
                      {c.render ? c.render(row) : cellValue(row, c)}
                    </td>
                  ))}
                  {actions && (
                    <td
                      className="fw-td--actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {actions(row)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* footer */}
      <div className="fw-table__foot">
        <span className="fw-table__count fw-num">
          {total.toLocaleString()} record{total === 1 ? "" : "s"}
        </span>
        <div className="fw-table__pages">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            aria-label="Previous page"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          {pageNumbers.map((n, i) =>
            n === "…" ? (
              <span key={`e${i}`} className="fw-table__ellipsis">…</span>
            ) : (
              <button
                key={n}
                type="button"
                data-active={n === page}
                onClick={() => setPage(n)}
              >
                {n + 1}
              </button>
            )
          )}
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
          <select
            value={limit}
            onChange={(e) => {
              setPage(0);
              setLimit(parseInt(e.target.value, 10));
            }}
            aria-label="Rows per page"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} rows
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
